import { isEqual } from 'es-toolkit';

const empty_to_null = (value) => {
    if (value == null || value === '')
        return null;
    return value;
};

const sorted_string_list = (value) => {
    if (!Array.isArray(value))
        return [];
    return [...value].filter(v => typeof v === 'string' && v !== '').sort();
};

const normalize_qty = (value) => {
    if (value == null || value === '')
        return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
};

export const sellers_is_unrestricted = (sellers) => {
    // Missing key = unrestricted / inherit. Present array (including []) = set.
    return !Array.isArray(sellers);
};

export const normalize_constraint_rules = (rules) => {
    const normalized = rules || {};
    return {
        not_before: empty_to_null(normalized.not_before),
        not_after: empty_to_null(normalized.not_after),
        only_for_roles: sorted_string_list(normalized.only_for_roles),
        only_for_salepoints: sorted_string_list(normalized.only_for_salepoints),
        only_for_tickettypes: sorted_string_list(normalized.only_for_tickettypes),
        exclude_tickettypes: sorted_string_list(normalized.exclude_tickettypes),
        only_for_categories: Array.isArray(normalized.only_for_categories)
            ? sorted_string_list(normalized.only_for_categories)
            : null,
    };
};

/**
 * A pricing constraint override is redundant when every constraint field
 * matches the tickettype defaults (including roles/categories). Pricing-only
 * tickettype filters count as non-redundant when they differ from defaults.
 */
export const is_redundant_constraint_override = (pricing, default_rules) => {
    if (!pricing)
        return true;

    const defaults = normalize_constraint_rules(default_rules);
    const rules = normalize_constraint_rules(pricing.rules || {});

    return (
        rules.not_before === defaults.not_before
        && rules.not_after === defaults.not_after
        && isEqual(rules.only_for_salepoints, defaults.only_for_salepoints)
        && isEqual(rules.only_for_roles, defaults.only_for_roles)
        && isEqual(rules.only_for_categories, defaults.only_for_categories)
        && isEqual(rules.only_for_tickettypes, defaults.only_for_tickettypes)
        && isEqual(rules.exclude_tickettypes, defaults.exclude_tickettypes)
    );
};

/**
 * Keep only override ids that already existed on the loaded tickettype.
 * Session-added overrides are excluded until save (avoids false positives
 * when a new override starts identical to the default, e.g. unlimited stock).
 */
export const filter_persisted_override_ids = (candidate_ids, persisted_ids) => {
    if (!Array.isArray(candidate_ids) || candidate_ids.length === 0)
        return [];
    if (persisted_ids == null)
        return candidate_ids;
    const persisted = persisted_ids instanceof Set
        ? persisted_ids
        : new Set(persisted_ids);
    return candidate_ids.filter(id => persisted.has(id));
};

export const find_redundant_constraint_overrides = (pricings, default_rules, override_ids, persisted_ids) => {
    const ids = filter_persisted_override_ids(
        override_ids || Object.keys(pricings || {}),
        persisted_ids
    );
    return ids.filter(id => is_redundant_constraint_override(pricings?.[id], default_rules));
};

/**
 * Stock override redundant when it mirrors the all-pricings pool and is not independent.
 */
export const is_redundant_stock_override = (override, all_pricings) => {
    if (!override || typeof override !== 'object')
        return true;
    if (override.independent_of_tickettype)
        return false;
    return normalize_qty(override.max_quantity) === normalize_qty(all_pricings?.max_quantity);
};

export const find_redundant_stock_overrides = (stocks, persisted_ids) => {
    const overrides = stocks?.overrides || {};
    const all_pricings = stocks?.all_pricings || {};
    return filter_persisted_override_ids(Object.keys(overrides), persisted_ids)
        .filter(id => is_redundant_stock_override(overrides[id], all_pricings));
};

const normalize_fields_block = (block) => ({
    requested: sorted_string_list(block?.requested),
    required: sorted_string_list(block?.required),
});

export const is_redundant_fields_override = (override_block, default_block) => (
    isEqual(normalize_fields_block(override_block), normalize_fields_block(default_block))
);

export const find_redundant_fields_overrides = (fields, persisted_ids) => {
    const default_block = fields?.default;
    if (!default_block)
        return [];
    return filter_persisted_override_ids(
        Object.keys(fields || {}).filter(id => id !== 'default'),
        persisted_ids
    ).filter(id => is_redundant_fields_override(fields[id], default_block));
};

const normalize_layout_map = (map) => {
    const out = {};
    Object.entries(map || {}).forEach(([key, value]) => {
        if (key === 'overrides')
            return;
        if (value == null || value === '')
            return;
        out[key] = value;
    });
    return out;
};

export const is_redundant_pdf_override = (override_map, default_map) => {
    const normalized_override = normalize_layout_map(override_map);
    if (Object.keys(normalized_override).length === 0)
        return true;
    return isEqual(normalized_override, normalize_layout_map(default_map));
};

export const find_redundant_pdf_overrides = (layouts, persisted_ids) => {
    const defaults = layouts || {};
    const overrides = layouts?.overrides || {};
    return filter_persisted_override_ids(Object.keys(overrides), persisted_ids)
        .filter(id => is_redundant_pdf_override(overrides[id], defaults));
};

const EMAIL_HOOKS = ['creation', 'activation', 'before_expiration', 'expiration', 'renewal', 'deletion'];

const normalize_email_hooks = (block) => {
    const out = {};
    EMAIL_HOOKS.forEach((hook) => {
        const value = block?.[hook];
        out[hook] = (value == null || value === '') ? null : value;
    });
    return out;
};

export const is_redundant_email_override = (override_block, default_block) => (
    isEqual(normalize_email_hooks(override_block), normalize_email_hooks(default_block))
);

export const find_redundant_email_overrides = (emails, persisted_ids) => {
    const defaults = emails?.default || {};
    return filter_persisted_override_ids(
        Object.keys(emails || {}).filter(id => id !== 'default'),
        persisted_ids
    ).filter(id => is_redundant_email_override(emails[id], defaults));
};
